// Framing the camera on a SET of elements (owner, 2026-09-20: ticking a
// level in the tracking tree selects everything under it and the view grows
// to hold the whole selection).
//
// Two jobs, deliberately separate:
//
//   unionBoxForUids  - where the selection IS. Read from the streaming
//     adapter's slots and the mesh's own snapshot matrices, never by
//     raycasting and never by walking meshesObject per uid. An element
//     inside a pooled or batched mesh contributes the box of ITS slot, not
//     of the whole tile - the distinction that made single-element framing
//     land on the origin before c84f39fb, and that a selection spanning
//     many tiles would hit on every one of them.
//
//   needsFraming     - whether to MOVE. This is the whole of the
//     auto-frame argument: "on" must not mean "the camera moves every time
//     you tick something". Someone working in one corner ticks what is in
//     front of them, it is already on screen at a usable size, and the
//     camera stays put. Someone ticking a level while looking at the far
//     end gets pulled out to see it. So the default is ON, defined as
//     "frame only when the selection is not already comfortably visible",
//     and the toggle a person can turn on means "always re-frame".
import { Box3, Vector3, Matrix4 } from 'three';
import { asFragmentRule } from '../loader/streaming/isolationRule';

// A JUDGEMENT, not a fact - change this NUMBER, not the logic around it.
// A selection smaller than this fraction of the view is technically on
// screen and not usefully visible: four pixels somewhere in a model. We
// decided framing that is help rather than interference. If someone
// reports that the camera moves for things they can already see, raise it;
// if they report ticking something and not being shown it, lower it.
// (Agreed with the leader, 2026-09-20.)
export const MIN_SCREEN_SHARE = 0.08;
// Keep a margin inside the edges: something touching the frame edge reads
// as cut off even when it technically projects inside.
export const EDGE_MARGIN = 0.02;

const _m = new Matrix4();
const _eb = new Box3();
const _b = new Box3();
const _v = new Vector3();

/**
 * Union of the world boxes of every slot holding one of `uids`.
 * Returns an empty Box3 when nothing is resolvable (tiles unloaded, uids
 * unknown) - the caller decides what that means rather than being handed a
 * box around the origin, which is the bug this module exists to avoid.
 */
/**
 * The same union, for a RULE rather than a list.
 *
 * "Frame what I ticked" had the identical gap the highlight had: a whole
 * category resolves to hashes plus a few thousand `singles`, and a
 * list-shaped frame framed only the singles. Nobody reported it because
 * isolating moves the camera anyway - which is exactly why it would have
 * sat there.
 *
 * Walks the adapter's tiles once and takes every slot the rule accepts,
 * so there is nothing to enumerate on the way in.
 *
 * THE RULE IS NORMALISED HERE, because this is where `.has` is actually
 * called. The page hands its plain object straight to the viewer as an
 * option - it never passes through the store - so a rule arriving here has
 * had nothing done to it. Taking it as given threw "t.has is not a
 * function" on the owner's screen, twice per tile, the moment he ticked a
 * category.
 */
// THE EXTENT OF ONE ELEMENT INSIDE ITS MESH.
//
// A POOLED mesh holds one fragment's geometry and repeats it per instance, so
// geometry.boundingBox IS the element's extent. A BATCHED mesh is many
// elements merged into ONE geometry, so geometry.boundingBox is the extent of
// the WHOLE BATCH - and using it gave every element in a batch the same, huge
// box. MEASURED on staging Project1 by the desktop agent: a conduit fitting a
// few inches across reported a 96.7 x 12.5 x 81.3 ft box, and two DIFFERENT
// fittings in the same batch reported the IDENTICAL box. The camera then
// framed the batch's centre, which is why F landed beside the selection for a
// single element as well as for many.
//
// tileMerge already records per-element bounds at merge time and hangs them on
// the mesh as userData.itemCentres / userData.itemRadii, so this is a lookup
// and not new work. They are a centre and a RADIUS - a sphere - so the box
// built from them is the element's bounding cube: slightly larger than its
// true AABB and never smaller, which is the safe direction for framing.
//
// ALL THREE readers in this file had the same bug, because all three reached
// for geometry.boundingBox. Fixing one would have left the others: the rule
// path framing wrongly, and rectangle selection projecting every element of a
// batch to one point.
// IS THIS ELEMENT ACTUALLY ON SCREEN?
//
// The three readers below used to answer this from the matrix alone:
//   if (m.elements[0] === 0 && m.elements[5] === 0 && m.elements[10] === 0) continue;
// with the comment "a zero-scale slot is a hidden element". That test could
// never be true, for two compounding reasons, and the owner's report of the
// rectangle being "back to not being accurate" is the result.
//
// ONE THE MATRIX IT READS IS THE WRONG ONE. These functions read
// mesh.userData.snapshotMatrices, which is the AUTHORITATIVE PLACEMENT - the
// value InstanceStateApplicator reads FROM when it decides to draw. Hiding
// writes its sentinel to the MESH instance matrix via setMatrixAt; the
// snapshot keeps the real placement, as it must, or a hidden element could
// never be shown again. So the snapshot never holds a hidden marker at all.
//
// TWO AND THE SENTINEL IS NOT ZERO ANYWAY. InstanceStateApplicator's
// ZERO_MATRIX is makeScale(0.0001, 0.0001, 0.0001), so even reading the live
// instance matrix, `=== 0` would not match it.
//
// So rectangle selection had NO knowledge of runtime visibility and took
// everything whose box fell in the rectangle, drawn or not. It asks the store
// now - the same source the renderer asks, so the two cannot disagree - and
// keeps a near-zero matrix check for an exporter-zeroed placement, which is a
// real thing and is not about hiding.
const HIDDEN_EPS = 1e-3;
function notDrawn(store, uid, hash, m) {
  if (store && typeof store.isHidden === 'function' && store.isHidden(uid, hash)) return true;
  const e = m.elements;
  // The length of each basis vector, so a ROTATED element is not mistaken for
  // a collapsed one - a 90-degree rotation puts a 0 on the diagonal.
  const sx = Math.hypot(e[0], e[1], e[2]);
  const sy = Math.hypot(e[4], e[5], e[6]);
  const sz = Math.hypot(e[8], e[9], e[10]);
  return sx < HIDDEN_EPS && sy < HIDDEN_EPS && sz < HIDDEN_EPS;
}

function elementCentre(mesh, index, out) {
  // Same two shapes as elementBox: a BatchedMesh answers directly, a merged
  // mesh through the centres tileMerge recorded.
  if (typeof mesh.getBoundingBoxAt === 'function' && index >= 0) {
    try {
      const b = mesh.getBoundingBoxAt(index, _eb);
      if (b && !b.isEmpty()) return b.getCenter(out);
    } catch (e) { /* fall through */ }
  }
  const c = mesh.userData && mesh.userData.itemCentres;
  if (c && index >= 0 && (index * 3 + 2) < c.length) {
    return out.set(c[index * 3], c[index * 3 + 1], c[index * 3 + 2]);
  }
  return null;
}

function elementBox(mesh, index, out) {
  // A THREE BatchedMesh KNOWS ITS OWN PER-ELEMENT BOX and this is the case
  // the first attempt missed entirely. tileMerge hangs itemCentres/itemRadii
  // on MERGED meshes; the conduit fittings live in a BatchedMesh built by
  // tileBatch, which names itself "batch:<tile>:<kind>" and sets NO such
  // userData. So the lookup below found nothing, fell through to the whole
  // geometry box, and the desktop agent's re-probe correctly reported the
  // box still 96.67 x 12.50 x 81.33 for two different fittings - the
  // batch's extent, with only the centres differing because the instance
  // matrices differ.
  // three's getBoundingBoxAt(id) answers for the id space that getMatrixAt
  // already uses here, which is the same slot.index.
  if (typeof mesh.getBoundingBoxAt === 'function' && index >= 0) {
    try {
      const b = mesh.getBoundingBoxAt(index, out);
      if (b && !b.isEmpty()) return b;
    } catch (e) { /* fall through to the readings below */ }
  }
  const c = mesh.userData && mesh.userData.itemCentres;
  const r = mesh.userData && mesh.userData.itemRadii;
  if (c && r && index >= 0 && index < r.length && (index * 3 + 2) < c.length) {
    const rad = r[index];
    if (rad >= 0) {
      out.min.set(c[index * 3] - rad, c[index * 3 + 1] - rad, c[index * 3 + 2] - rad);
      out.max.set(c[index * 3] + rad, c[index * 3 + 1] + rad, c[index * 3 + 2] + rad);
      return out;
    }
  }
  return null;
}

export function unionBoxForRule(adapter, rawRule, { box = new Box3(), limit = 200000 } = {}) {
  // The state store, for the one question the matrix cannot answer: is this
  // element actually drawn right now. Absent on an older adapter, and then
  // notDrawn falls back to the matrix alone - which is what it did before.
  const _store = adapter && adapter.store;
  box.makeEmpty();
  const rule = asFragmentRule(rawRule);
  if (!adapter || !rule || !adapter.activeByTile) return box;
  const geoBoxes = new Map();
  let seen = 0;
  adapter.activeByTile.forEach((tileEntry) => {
    if (!tileEntry || !tileEntry.slotsByUid) return;
    tileEntry.slotsByUid.forEach((slots, uid) => {
      if (!slots || seen > limit) return;
      for (const slot of slots) {
        if (!slot || !slot.mesh) continue;
        const mesh = slot.mesh;
        const hash = mesh.userData && mesh.userData.hash;
        if (!rule.has(uid, hash)) continue;
        seen += 1;
        const snapshot = mesh.userData && mesh.userData.snapshotMatrices;
        if (!snapshot) continue;
        let gb = geoBoxes.get(mesh);
        if (gb === undefined) {
          const g = mesh.geometry;
          if (g && !g.boundingBox) g.computeBoundingBox();
          gb = g && g.boundingBox ? g.boundingBox : null;
          geoBoxes.set(mesh, gb);
        }
        if (!gb) continue;
        _m.fromArray(snapshot, slot.index * 16);
        if (notDrawn(_store, uid, mesh.userData && mesh.userData.hash, _m)) continue;
        // Per-element extent where the mesh records one (a BATCH); the whole
        // geometry box only where that IS the element (a POOL).
        if (elementBox(mesh, slot.index, _eb)) _b.copy(_eb);
        else _b.copy(gb);
        _b.applyMatrix4(_m);
        if (mesh.matrixWorld) _b.applyMatrix4(mesh.matrixWorld);
        box.union(_b);
      }
    });
  });
  return box;
}

/**
 * The elements the box TOUCHES on screen - the construction tracking page's box
 * select (owner, 2026-09-22: "every fragment in there gets selected").
 *
 * IT USED TO TEST THE CENTRE, and that is the bug the owner reported on
 * 2026-09-29: "the control selection box is not picking up all elements that
 * are inside the box". A duct run, a rack or a tray crossing the rectangle has
 * its centre somewhere else entirely, so it was skipped while being plainly
 * inside the box - and the longer the element, the more likely it was missed.
 * Every element the box covers ANY PART OF is now selected, which is what a
 * marquee means everywhere else.
 *
 * Screen footprint from the element's own eight box corners, not from a
 * projected radius: a projected sphere is wrong under perspective in exactly
 * the case that matters here, a long thin element seen end-on.
 *
 * Reads the same active slots unionBoxForUids reads, so it sees exactly what
 * is drawn: a zero-scale (hidden or isolated-away) slot is skipped, and an
 * element that is not streamed in is not a candidate - the caller says so.
 * `rect` is in canvas pixels {x0, y0, x1, y1}; `size` is the canvas {width,
 * height}.
 *
 * BEHIND THE CAMERA IS NOT SIMPLY "OUTSIDE". An element straddling the camera
 * plane has corners with w < 0 that project to nonsense, so a box that mixes
 * them would balloon: a corner behind the camera marks the element as crossing
 * the plane and it is taken as touching, which is the honest answer for
 * something the camera is inside of. Wholly behind is skipped as before.
 */
// NO DEFAULT CAP (owner, 2026-10-24: a box on SpaceX caught exactly 200,000
// and "many don't get selected"). The walk is in load order, not screen
// order, so a cap kept an arbitrary 200,000 of the elements inside the box
// and dropped the rest with nothing said. The walk visits every active
// element whatever the cap; only the list was ever saved by it. A caller
// that truly needs a bound passes `limit` and must say it cut.
export function uidsInScreenRect(adapter, camera, rect, size, { limit = Infinity } = {}) {
  // The state store, for the one question the matrix cannot answer: is this
  // element actually drawn right now. Absent on an older adapter, and then
  // notDrawn falls back to the matrix alone - which is what it did before.
  const _store = adapter && adapter.store;
  const out = [];
  if (!adapter || !adapter.activeUidOwner || !adapter.activeByTile || !camera || !rect || !size) return out;
  const x0 = Math.min(rect.x0, rect.x1); const x1 = Math.max(rect.x0, rect.x1);
  const y0 = Math.min(rect.y0, rect.y1); const y1 = Math.max(rect.y0, rect.y1);
  const geoBoxes = new Map();
  const c = new Vector3();
  const _corner = new Vector3();
  for (const [uid, owner] of adapter.activeUidOwner) {
    if (out.length >= limit) break;
    const tileEntry = owner && adapter.activeByTile.get(owner.tileKey);
    const slots = tileEntry && tileEntry.slotsByUid && tileEntry.slotsByUid.get(uid);
    if (!slots || !slots.length) continue;
    const slot = slots.find((sl) => sl && sl.mesh && sl.mesh.userData && sl.mesh.userData.snapshotMatrices) || null;
    if (!slot) continue;
    const mesh = slot.mesh;
    let gb = geoBoxes.get(mesh);
    if (gb === undefined) {
      const g = mesh.geometry;
      if (g && !g.boundingBox) g.computeBoundingBox();
      gb = g && g.boundingBox ? g.boundingBox : null;
      geoBoxes.set(mesh, gb);
    }
    if (!gb) continue;
    _m.fromArray(mesh.userData.snapshotMatrices, slot.index * 16);
    // Hidden, or collapsed by the exporter - either way not in the rectangle.
    if (notDrawn(_store, uid, mesh.userData && mesh.userData.hash, _m)) continue;
    // THE ELEMENT'S OWN EXTENT where the mesh records one (a BATCH knows its
    // per-element box; a merged mesh has itemCentres/itemRadii), and the whole
    // geometry box only where that IS the element (a POOL). Without this
    // distinction every element of a batch shares one box and a rectangle
    // takes all of it or none.
    if (elementBox(mesh, slot.index, _eb)) _b.copy(_eb);
    else _b.copy(gb);
    _b.applyMatrix4(_m);
    if (mesh.matrixWorld) _b.applyMatrix4(mesh.matrixWorld);

    let minX = Infinity; let maxX = -Infinity; let minY = Infinity; let maxY = -Infinity;
    let anyInFront = false; let crossesCamera = false;
    for (let i = 0; i < 8; i += 1) {
      _corner.set(
        (i & 1) ? _b.max.x : _b.min.x,
        (i & 2) ? _b.max.y : _b.min.y,
        (i & 4) ? _b.max.z : _b.min.z,
      );
      _corner.project(camera);
      if (_corner.z < -1) { crossesCamera = true; continue; }   // behind the near plane
      if (_corner.z > 1) continue;                              // past the far plane
      anyInFront = true;
      const px = ((_corner.x + 1) / 2) * size.width;
      const py = ((1 - _corner.y) / 2) * size.height;
      if (px < minX) minX = px;
      if (px > maxX) maxX = px;
      if (py < minY) minY = py;
      if (py > maxY) maxY = py;
    }
    if (!anyInFront) continue;                                  // wholly behind or clipped away
    // Something the camera is inside of is touching whatever the box covers.
    if (crossesCamera) { out.push(uid); continue; }
    // OVERLAP, not containment: an element the box covers any part of is in.
    if (maxX >= x0 && minX <= x1 && maxY >= y0 && minY <= y1) out.push(uid);
  }
  return out;
}

export function unionBoxForUids(adapter, uids, { box = new Box3(), limit = 200000, deferred = null } = {}) {
  // The state store, for the one question the matrix cannot answer: is this
  // element actually drawn right now. Absent on an older adapter, and then
  // notDrawn falls back to the matrix alone - which is what it did before.
  const _store = adapter && adapter.store;
  box.makeEmpty();
  if (!adapter || !uids || !adapter.activeByTile) return box;

  // Rows the budget is HOLDING have no active slot, so the loop below skips
  // them and the box comes back empty. That is why F could not frame a
  // deferred element at all - the owner's original report, since a small
  // fitting seen from far away is exactly what gets held. Collect the misses
  // and ask ONCE, after the loop, where they would be.
  const missed = [];

  // One geometry box per mesh, not per instance.
  const geoBoxes = new Map();
  let seen = 0;

  for (const uid of uids) {
    if (++seen > limit) break;
    const entries = adapter.getActiveSlotsForUid ? adapter.getActiveSlotsForUid(uid) : null;
    if (!entries || !entries.length) { missed.push(uid); continue; }

    // Only the ACTIVE slot has a real matrix; duplicates in other tiles are
    // parked at zero scale and would drag the box toward nothing.
    const owner = adapter.activeUidOwner && adapter.activeUidOwner.get(uid);
    const activeTileKey = owner ? owner.tileKey : null;

    for (const { tileKey, slotIndex } of entries) {
      if (activeTileKey && tileKey !== activeTileKey) continue;
      const tileEntry = adapter.activeByTile.get(tileKey);
      if (!tileEntry) continue;
      const slots = tileEntry.slotsByUid.get(uid);
      const slot = slots && slots[slotIndex];
      if (!slot || !slot.mesh) continue;

      const mesh = slot.mesh;
      const snapshot = mesh.userData && mesh.userData.snapshotMatrices;
      if (!snapshot) continue;

      let gb = geoBoxes.get(mesh);
      if (gb === undefined) {
        const g = mesh.geometry;
        if (g && !g.boundingBox) g.computeBoundingBox();
        gb = g && g.boundingBox ? g.boundingBox : null;
        geoBoxes.set(mesh, gb);
      }
      if (!gb) continue;

      _m.fromArray(snapshot, slot.index * 16);
      // A zero-scale slot is a hidden element; it has no place in the box.
      if (notDrawn(_store, uid, mesh.userData && mesh.userData.hash, _m)) continue;
      // Per-element extent where the mesh records one (a BATCH); the whole
      // geometry box only where that IS the element (a POOL).
      if (elementBox(mesh, slot.index, _eb)) _b.copy(_eb);
      else _b.copy(gb);
      _b.applyMatrix4(_m);
      if (mesh.matrixWorld) _b.applyMatrix4(mesh.matrixWorld);
      box.union(_b);
    }
  }

  // THE HELD ROWS. A sphere from the row's own placement and the fragment
  // radius - coarser than the true box and NEVER SMALLER, which is the safe
  // direction: the camera pulls back slightly rather than cutting the element
  // off. Only asked for when something was actually missed, so a selection of
  // resident elements costs nothing.
  if (missed.length && typeof deferred === 'function') {
    const places = deferred(missed);
    if (places && typeof places.get === 'function') {
      for (const uid of missed) {
        const p = places.get(String(uid));
        if (!p) continue;
        const r = p.r > 0 ? p.r : 0;
        _eb.min.set(p.x - r, p.y - r, p.z - r);
        _eb.max.set(p.x + r, p.y + r, p.z + r);
        box.union(_eb);
      }
    }
  }
  return box;
}

/**
 * Should the camera move for this box?
 * Returns null when it should stay put, otherwise a reason:
 *   'empty'     nothing to frame (caller should do nothing)
 *   'behind'    some of it is behind the camera
 *   'offscreen' some of it projects outside the viewport
 *   'too-small' all on screen, but too small to actually see
 * Reasons rather than a boolean so the decision can be logged and tested
 * without re-deriving it.
 */
export function needsFraming(box, camera, viewport, opts = {}) {
  const margin = opts.edgeMargin === undefined ? EDGE_MARGIN : opts.edgeMargin;
  const minShare = opts.minScreenShare === undefined ? MIN_SCREEN_SHARE : opts.minScreenShare;
  if (!box || box.isEmpty()) return 'empty';
  if (!camera) return 'offscreen';

  const min = box.min;
  const max = box.max;
  let minX = Infinity; let maxX = -Infinity;
  let minY = Infinity; let maxY = -Infinity;
  let behind = false;
  let outside = false;

  for (let i = 0; i < 8; i++) {
    _v.set(i & 1 ? max.x : min.x, i & 2 ? max.y : min.y, i & 4 ? max.z : min.z);
    _v.project(camera);
    // project() divides by w; a point behind the camera comes back with
    // z outside [-1, 1], and its x/y are meaningless, so stop reading them.
    if (_v.z < -1 || _v.z > 1) { behind = true; break; }
    if (_v.x < -1 + margin || _v.x > 1 - margin || _v.y < -1 + margin || _v.y > 1 - margin) outside = true;
    if (_v.x < minX) minX = _v.x;
    if (_v.x > maxX) maxX = _v.x;
    if (_v.y < minY) minY = _v.y;
    if (_v.y > maxY) maxY = _v.y;
  }

  if (behind) return 'behind';
  if (outside) return 'offscreen';

  // NDC spans 2 units across the viewport, so half the NDC extent is the
  // share of the screen the selection covers.
  const shareX = (maxX - minX) / 2;
  const shareY = (maxY - minY) / 2;
  if (Math.max(shareX, shareY) < minShare) return 'too-small';
  return null;
}

/**
 * The whole decision in one call, for the viewer to use.
 * mode: 'auto'   - move only when needsFraming says so (the default)
 *       'always' - move whenever the selection changes (the toggle)
 *       'never'  - never move
 * Returns { box, reason, shouldFrame }.
 */
export function planFraming(adapter, uids, camera, { mode = 'auto', box, rule, ...opts } = {}) {
  // A rule frames without a list; `uids` still works and is unchanged.
  // Normalised before the CHOICE as well as inside the union, so a rule
  // that is merely rule-SHAPED and holds nothing falls back to the list
  // rather than framing on an empty box.
  const matcher = asFragmentRule(rule);
  const b = matcher
    ? unionBoxForRule(adapter, matcher, { box, limit: opts.limit })
    : unionBoxForUids(adapter, uids, { box, limit: opts.limit, deferred: opts.deferred });
  if (b.isEmpty()) return { box: b, reason: 'empty', shouldFrame: false };
  if (mode === 'never') return { box: b, reason: null, shouldFrame: false };
  if (mode === 'always') return { box: b, reason: 'always', shouldFrame: true };
  const reason = needsFraming(b, camera, null, opts);
  return { box: b, reason, shouldFrame: reason !== null && reason !== 'empty' };
}
